export interface CourseTypeOption {
  value: string;
  label: string;
  /** Sous-chaîne (en minuscules) recherchée dans ClassDescription.Name. Ignoré si `excludes` est défini. */
  match?: string;
  /**
   * Filtre par exclusion : le cours est retenu si son nom ne contient AUCUNE de ces
   * sous-chaînes (en minuscules). Sert aux types « fourre-tout » définis en négatif
   * (ex. Dynamo « Signature » = tout ce qui n'est ni Strong ni Dance).
   */
  excludes?: string[];
}

export type Brand = 'dynamo' | 'riise' | 'punch';

/**
 * Types de cours proposés au filtre « Type de cours » du planning, par MARQUE.
 *
 * Le filtre est volontairement basé sur le NOM du cours (ClassDescription.Name),
 * comme l'historique Riise : un cours est retenu si son nom contient `match`
 * (comparaison en minuscules). Les noms de cours sont les mêmes d'une ville à
 * l'autre (vérifié le 12/10/2026 sur les classdescriptions actives des 10 sites),
 * donc un seul jeu de types par marque suffit ; le site est rattaché à sa marque
 * par `BRAND_BY_SITE`. Un site absent de cette table n'a pas de filtre.
 */
export const COURSE_TYPES_BY_BRAND: Record<Brand, CourseTypeOption[]> = {
  // Riise : RIISE YOGA, RIISE PILATES, RIISE REFORMER (+ playlists « REFORMER … »),
  // RIISE BARRE. « Barre » est une 4e verticale lancée en septembre 2026 (Paris
  // Charlot, Annecy) ; aucun autre nom de cours Riise ne contient « barre ».
  riise: [
    { value: 'yoga', label: 'Yoga', match: 'yoga' },
    { value: 'pilates', label: 'Pilates', match: 'pilates' },
    { value: 'reformer', label: 'Reformer', match: 'reformer' },
    { value: 'barre', label: 'Barre', match: 'barre' },
  ],
  // Dynamo : match volontairement préfixé « dynamo » pour ne pas attraper les
  // playlists (ex. « LAST DANCE », « Electro Dance ») ni « Strong Arms ».
  // « Signature » n'est pas un nom de cours réel : c'est le ride Dynamo par défaut
  // (DYNAMO, DYNAMO*, DYNAMO ESPRIT…) + toutes les spéciales musicales, donc défini
  // en négatif = tout ce qui n'est ni Strong ni Dance (variantes DÉCOUVERTE incluses).
  // Toutes les villes n'ont pas Strong et Dance au planning (Bordeaux n'a ni l'un ni
  // l'autre au 12/10/2026) : l'option existe quand même, elle renvoie alors zéro cours.
  dynamo: [
    { value: 'signature', label: 'Signature', excludes: ['dynamo strong', 'dynamo dance'] },
    { value: 'strong', label: 'Strong', match: 'dynamo strong' },
    { value: 'dance', label: 'Dance', match: 'dynamo dance' },
  ],
  // Punch : deux pratiques seulement, le Lagree (LAGREE STRONG, LAGREE STRONG*, Paris
  // uniquement) et tout le reste, qui relève de la boxe/bootcamp (BOXE/BOOTCAMP,
  // PUNCH HIGH-INTENSITY, PUNCH BOOTY & LEGS, playlists « 100% TECHNO / POP / RNB »…).
  // « Boxe / Bootcamp » est donc défini en négatif pour couvrir les playlists et les
  // futurs noms de cours boxe sans avoir à maintenir la liste.
  punch: [
    { value: 'boxe', label: 'Boxe / Bootcamp', excludes: ['lagree'] },
    { value: 'lagree', label: 'Lagree Strong', match: 'lagree' },
  ],
};

/** Marque de chaque site Mindbody de prod (= apps/server/Config.php, clubs par région). */
export const BRAND_BY_SITE: Record<string, Brand> = {
  // Paris
  '5736526': 'dynamo',
  '5736555': 'riise',
  '5736551': 'punch',
  // Marseille
  '5745580': 'dynamo',
  '5746614': 'riise',
  '5745076': 'punch',
  // Bordeaux
  '5745241': 'dynamo',
  '5746279': 'riise',
  // Annecy
  '5748709': 'dynamo',
  '5748656': 'riise',
  '5757102': 'punch',
  // Lyon
  '5756554': 'dynamo',
  '5757538': 'riise',
};

/** Marque d'un site, ou undefined si le site n'est pas connu. */
export function brandOfSite(siteId?: string | number): Brand | undefined {
  if (siteId === undefined || siteId === null) return undefined;
  return BRAND_BY_SITE[String(siteId)];
}

/** Options de type de cours pour un site (tableau vide si le site n'a pas de marque connue). */
export function getCourseTypeOptions(siteId?: string | number): CourseTypeOption[] {
  const brand = brandOfSite(siteId);
  return brand ? COURSE_TYPES_BY_BRAND[brand] : [];
}

/**
 * Le filtre « Type de cours » doit-il être affiché ?
 * Vrai si le toggle back-office est activé ET que le site courant a des types définis.
 */
export function isCourseTypeFilterEnabled(): boolean {
  return (
    Boolean(mondayMindbody?.customization?.enableCourseTypeFilter) &&
    getCourseTypeOptions(mondayMindbody?.siteId).length > 0
  );
}

/** Un cours correspond-il à au moins un des types sélectionnés ? (true si aucune sélection) */
export function matchesCourseType(className: string, selectedTypes: string[], siteId?: string | number): boolean {
  if (!Array.isArray(selectedTypes) || selectedTypes.length === 0) return true;
  const options = getCourseTypeOptions(siteId);
  // Normalise les astérisques (DYNAMO*, DYNAMO STRONG*, DYNAMO* STRONG…) en espaces
  // pour que la position du « * » ne change pas le résultat du match.
  const name = (className || '')
    .toLowerCase()
    .replace(/\*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return selectedTypes.some((type) => {
    const option = options.find((o) => o.value === type);
    if (!option) return false;
    if (option.excludes) return !option.excludes.some((ex) => name.includes(ex));
    return option.match ? name.includes(option.match) : false;
  });
}
